import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Avatar,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  LocationOn as LocationsIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Payment as PaymentIcon,
  UploadFile as UploadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

interface WorkingDay {
  works: boolean;
  open: string;
  close: string;
  break_start?: string;
  break_end?: string;
}

interface Location {
  id: number;
  name: string;
  description?: string;
  logo_url?: string;
  is_active: boolean;
  legal_address?: string;
  physical_address: string;
  phone: string;
  additional_phone?: string;
  email: string;
  website?: string;
  social_media?: Record<string, string>;
  working_hours?: Record<string, WorkingDay>;
  payment_methods?: string[];
  sms_notifications_enabled: boolean;
  sms_template?: string;
  created_at: string;
  updated_at?: string;
}

interface PaymentMethod {
  value: string;
  label: string;
}

const getApiBase = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (!envBase) return "/api";
  if (
    envBase.startsWith("http://") &&
    typeof window !== "undefined" &&
    window.location.protocol === "https:"
  ) {
    return envBase.replace(/^http:\/\//, "https://");
  }
  return envBase;
};

const API_BASE = getApiBase();

const asErrorMessage = (detail: any) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return String(detail);
  }
};

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Понедельник' },
  { key: 'tuesday', label: 'Вторник' },
  { key: 'wednesday', label: 'Среда' },
  { key: 'thursday', label: 'Четверг' },
  { key: 'friday', label: 'Пятница' },
  { key: 'saturday', label: 'Суббота' },
  { key: 'sunday', label: 'Воскресенье' },
];

const DEFAULT_WORKING_HOURS = {
  monday: { works: true, open: "09:00", close: "21:00" },
  tuesday: { works: true, open: "09:00", close: "21:00" },
  wednesday: { works: true, open: "09:00", close: "21:00" },
  thursday: { works: true, open: "09:00", close: "21:00" },
  friday: { works: true, open: "09:00", close: "21:00" },
  saturday: { works: true, open: "09:00", close: "21:00" },
  sunday: { works: false, open: "09:00", close: "21:00" },
};

export default function LocationsPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Location[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);

  // Form states
  const [formTab, setFormTab] = useState(0);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    logo_url: string;
    legal_address: string;
    physical_address: string;
    phone: string;
    additional_phone: string;
    email: string;
    website: string;
    social_media: Record<string, string>;
    working_hours: Record<string, WorkingDay>;
    payment_methods: string[];
    sms_notifications_enabled: boolean;
    sms_template: string;
    is_active: boolean;
  }>({
    name: "",
    description: "",
    logo_url: "",
    legal_address: "",
    physical_address: "",
    phone: "",
    additional_phone: "",
    email: "",
    website: "",
    social_media: { instagram: "", vk: "", telegram: "" },
    working_hours: DEFAULT_WORKING_HOURS,
    payment_methods: ["cash", "card"],
    sms_notifications_enabled: true,
    sms_template: "",
    is_active: true,
  });

  useEffect(() => {
    fetchLocations();
    fetchPaymentMethods();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/locations/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
        setError("");
      } else {
        const errorData = await response.json();
        setError(asErrorMessage(errorData.detail) || "Ошибка загрузки локаций");
      }
      
    } catch (error) {
      console.error("Error fetching locations:", error);
      setError("Ошибка подключения к серверу");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/locations/payment-methods`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data);
      }
    } catch (err) {
      console.error("Failed to fetch payment methods:", err);
    }
  };

  const handleCreateLocation = async () => {
    try {
      const token = localStorage.getItem("token");
      
      const url = editingLocation 
        ? `${API_BASE}/locations/${editingLocation.id}/`
        : `${API_BASE}/locations/`;
      const method = editingLocation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSuccess(editingLocation ? "Место обновлено успешно" : "Место создано успешно");
        setDialogOpen(false);
        setEditingLocation(null);
        resetForm();
        fetchLocations(); // Обновляем список
      } else {
        const errorData = await response.json();
        setError(asErrorMessage(errorData.detail) || "Ошибка сохранения места");
      }
      
    } catch (err) {
      console.error("Failed to save location:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleDeleteLocation = async (location: Location) => {
    if (!window.confirm(`Вы уверены, что хотите удалить место "${location.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/locations/${location.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setSuccess("Место удалено успешно");
        fetchLocations(); // Обновляем список
      } else {
        const errorData = await response.json();
        setError(asErrorMessage(errorData.detail) || "Ошибка удаления места");
      }
      
    } catch (err) {
      console.error("Failed to delete location:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      description: location.description || "",
      logo_url: location.logo_url || "",
      legal_address: location.legal_address || "",
      physical_address: location.physical_address,
      phone: location.phone,
      additional_phone: location.additional_phone || "",
      email: location.email,
      website: location.website || "",
      social_media: location.social_media || { instagram: "", vk: "", telegram: "" },
      working_hours: location.working_hours || DEFAULT_WORKING_HOURS,
      payment_methods: location.payment_methods || ["cash", "card"],
      sms_notifications_enabled: location.sms_notifications_enabled,
      sms_template: location.sms_template || "",
      is_active: location.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      logo_url: "",
      legal_address: "",
      physical_address: "",
      phone: "",
      additional_phone: "",
      email: "",
      website: "",
      social_media: { instagram: "", vk: "", telegram: "" },
      working_hours: DEFAULT_WORKING_HOURS,
      payment_methods: ["cash", "card"],
      sms_notifications_enabled: true,
      sms_template: "",
      is_active: true,
    });
    setFormTab(0);
  };

  const handleOpenDialog = () => {
    setEditingLocation(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleWorkingHoursChange = (day: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours?.[day],
          [field]: value
        }
      }
    }));
  };

  const handleSocialMediaChange = (platform: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value
      }
    }));
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header title="Места" />
      
      <Box sx={{ p: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate("/")}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Главная
          </Link>
          <Typography color="text.primary">
            Места
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: "text.primary" }}>
            Места (Филиалы)
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Добавить место
          </Button>
        </Box>

        {/* Locations Grid */}
        <Grid container spacing={3}>
          {locations.map((location) => (
            <Grid item xs={12} sm={6} md={4} key={location.id}>
              <Card
                sx={{
                  transition: "all 0.3s ease",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    borderColor: "#2196F3",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        src={location.logo_url}
                        sx={{ width: 48, height: 48, bgcolor: "#2196F3" }}
                      >
                        <LocationsIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                          {location.name}
                        </Typography>
                        <Chip
                          label={location.is_active ? "Активно" : "Неактивно"}
                          color={location.is_active ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditLocation(location)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteLocation(location)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      📍 {location.physical_address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      📞 {location.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ✉️ {location.email}
                    </Typography>
                  </Box>

                  {location.description && (
                    <Typography variant="body2" color="text.secondary">
                      {location.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingLocation ? "Редактировать место" : "Добавить место"}
          </DialogTitle>
          <DialogContent>
            <Tabs value={formTab} onChange={(e, v) => setFormTab(v)} sx={{ mb: 3 }}>
              <Tab label="Основная информация" />
              <Tab label="Контакты" />
              <Tab label="Расписание" />
              <Tab label="Настройки" />
            </Tabs>

            {/* Tab 1: Basic Info */}
            {formTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Название места"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Описание"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Юридический адрес"
                    value={formData.legal_address}
                    onChange={(e) => setFormData({ ...formData, legal_address: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Физический адрес"
                    required
                    value={formData.physical_address}
                    onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {/* Tab 2: Contacts */}
            {formTab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Телефон"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Дополнительный телефон"
                    value={formData.additional_phone}
                    onChange={(e) => setFormData({ ...formData, additional_phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Сайт"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Социальные сети
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Instagram"
                        value={formData.social_media.instagram}
                        onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="VK"
                        value={formData.social_media.vk}
                        onChange={(e) => handleSocialMediaChange('vk', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Telegram"
                        value={formData.social_media.telegram}
                        onChange={(e) => handleSocialMediaChange('telegram', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Tab 3: Working Hours */}
            {formTab === 2 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Часы работы
                </Typography>
                {DAYS_OF_WEEK.map((day) => (
                  <Box key={day.key} sx={{ mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.working_hours[day.key]?.works || false}
                              onChange={(e) => handleWorkingHoursChange(day.key, 'works', e.target.checked)}
                            />
                          }
                          label={day.label}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          type="time"
                          label="Открытие"
                          disabled={!formData.working_hours[day.key]?.works}
                          value={formData.working_hours[day.key]?.open ?? "09:00"}
                          onChange={(e) => handleWorkingHoursChange(day.key, 'open', e.target.value)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          type="time"
                          label="Закрытие"
                          disabled={!formData.working_hours[day.key]?.works}
                          value={formData.working_hours[day.key]?.close ?? "21:00"}
                          onChange={(e) => handleWorkingHoursChange(day.key, 'close', e.target.value)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}

            {/* Tab 4: Settings */}
            {formTab === 3 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Способы оплаты
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      multiple
                      value={formData.payment_methods}
                      onChange={(e) => setFormData({ ...formData, payment_methods: e.target.value as string[] })}
                      input={<OutlinedInput label="Способы оплаты" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={paymentMethods.find(m => m.value === value)?.label || value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {paymentMethods.map((method) => (
                        <MenuItem key={method.value} value={method.value}>
                          <Checkbox checked={formData.payment_methods.indexOf(method.value) > -1} />
                          <ListItemText primary={method.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.sms_notifications_enabled}
                        onChange={(e) => setFormData({ ...formData, sms_notifications_enabled: e.target.checked })}
                      />
                    }
                    label="Включить SMS уведомления"
                  />
                </Grid>
                {formData.sms_notifications_enabled && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Шаблон SMS"
                      multiline
                      rows={3}
                      value={formData.sms_template}
                      onChange={(e) => setFormData({ ...formData, sms_template: e.target.value })}
                      helperText="Доступные переменные: {дата}, {время}, {мастер}, {адрес}, {клиент}"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Активное место"
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleCreateLocation} variant="contained">
              {editingLocation ? "Сохранить" : "Создать"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alerts */}
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")}>
          <Alert onClose={() => setError("")} severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess("")}>
          <Alert onClose={() => setSuccess("")} severity="success" sx={{ width: "100%" }}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
